import { createContext, useCallback, useContext, useMemo } from "react";
import type { PermissionNames, PermissionOperations } from "~enums/permissions";
import type { UserRoles } from "~enums/userRoles";
import useGetCurrentUserPermissionScope from "~hooks/useGetCurrentUserPermissionScope";

type UserPermissionsContextType = {
  hasOrganizationAccess: boolean;
  permissions: Record<string, Array<string>>;
  userRoles: UserRoles[];
  isLoadingPermissions: boolean;
  hasPermission: (permissionName: PermissionNames, permissionOperation: PermissionOperations) => boolean;
  hasEveryPermission: (permissionNames: PermissionNames[], permissionOptions: PermissionOperations[]) => boolean;
};

const UserPermissionsContext = createContext<UserPermissionsContextType | null>(null);

export const UserPermissionsProvider = ({ children }) => {
  const { data: scopeData, isLoading: isLoadingPermissions } = useGetCurrentUserPermissionScope();
  const userRoles = scopeData?.scope?.userRoles || [];
  const rbacMatrix = useMemo<Record<string, Array<string>>>(
    () =>
      (scopeData?.scope?.rbacLines || []).reduce((accumulator, currentValue) => {
        accumulator[currentValue.resource] = currentValue.operations;
        return accumulator;
      }, {}),
    [scopeData?.scope?.rbacLines],
  );
  const hasOrganizationAccess = useMemo(() => Object.keys(rbacMatrix).length > 0, [rbacMatrix]);

  const hasPermission = useCallback(
    (permissionName: PermissionNames, permissionOperation: PermissionOperations): boolean => {
      if (permissionName in rbacMatrix) {
        return rbacMatrix[permissionName].includes(permissionOperation);
      }
      return false;
    },
    [rbacMatrix],
  );

  const hasEveryPermission = useCallback(
    (permissionNames: PermissionNames[], permissionOptions: PermissionOperations[]): boolean =>
      permissionNames.every((key) => {
        if (key in rbacMatrix) {
          return permissionOptions.every((option) => rbacMatrix[key]?.includes(option));
        }
        return false;
      }),
    [rbacMatrix],
  );

  return (
    <UserPermissionsContext.Provider
      value={{
        hasOrganizationAccess,
        permissions: rbacMatrix,
        isLoadingPermissions,
        hasPermission,
        hasEveryPermission,
        userRoles,
      }}
    >
      {children}
    </UserPermissionsContext.Provider>
  );
};

export const useUserPermissions = (): UserPermissionsContextType => {
  const context = useContext(UserPermissionsContext);
  if (!context) {
    throw new Error("useUserPermissions must be used within a UserPermissionsProvider");
  }
  return context;
};
